import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { QueryRef } from 'apollo-angular';
import { DateTime } from 'luxon';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import {
  LotsGQL,
  LotSource,
  LotsQuery,
  LotsQueryVariables,
  LotTypeEnum,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';

import { DateRange, getDaysRange, getTypeSort, toLocalIsoString } from '../../../../const';
import { AraiKenkiLotModalComponent } from '../arai-kenki-lot-modal/arai-kenki-lot-modal.component';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-arai-kenki-lots-list',
  templateUrl: './arai-kenki-lots-list.component.html',
  styleUrls: ['./arai-kenki-lots-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AraiKenkiLotsListComponent implements OnInit, OnDestroy {
  lotsListQueryRef: QueryRef<LotsQuery, LotsQueryVariables>;
  data: any = {
    total: 0,
    nodes: [],
  };
  loading = true;
  total = 0;

  LotTypeEnum = LotTypeEnum;
  LotSource = LotSource;

  form: FormGroup;
  lastDay: Date;
  dateRange: DateRange[] = [];
  selectedDays: string[] = [];
  dumpVersion: string;

  page = 1;
  pageSize = 20;

  private destroy$ = new Subject<void>();

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private lotsGQL: LotsGQL,
    private modalService: NzModalService,
    private translate: TranslateService,
  ) {
    this.initForm();
    this.initRouteQueryParamsByLotsSub();
  }

  initRouteQueryParamsByLotsSub(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.loading = true;
      this.page = +params.page || 1;
      this.pageSize = +params.pageSize || this.pageSize;

      this.form.patchValue(this.getFilterFormState(params));
      this.initDate();

      const sort = {};

      if (params.sort && params.sortType) {
        sort[params.sort] = {
          direction: params.sortType,
        };
      }

      const queryFilters: LotsQueryVariables = {
        first: this.pageSize,
        offset: (this.page - 1) * this.pageSize,
        filter: {
          lotCompanyEn: {
            eq: params.company,
          },
          lotModelEn: {
            eq: params.model,
          },
          lotYear: {
            gte: +params.yearFrom,
            lte: +params.yearTo,
          },
          lotMileageNum: {
            gte: +params.mileageFrom ? +params.mileageFrom : null,
            lte: +params.mileageTo ? +params.mileageTo : null,
          },
          lotSource: {
            eq: LotSource.Manual,
          },
          lotColorEn: {
            eq: params.color,
          },
          lotRatingEn: {
            eq: params.rating,
          },
          lotCode: {
            eq: params.code,
          },
          lotAuctionName: {
            eq: 'ARAI KENKI',
          },
          lotAuctionDate: {
            eq: params.auctionDate || this.getTuesday(),
          },
        },
        sort,
      };
      if (!this.lotsListQueryRef) {
        this.lotsListQueryRef = this.lotsGQL.watch(queryFilters, {
          fetchPolicy: 'network-only',
        });
        this.lotsListQueryRef.valueChanges.subscribe(
          (result) => {
            this.loading = false;
            this.data = result.data.lots;
            this.total = result.data.lots.total;
          },
          (error) => {
            console.log(error);
            this.loading = false;
          },
        );
      } else {
        this.lotsListQueryRef.refetch(queryFilters).finally(() => {
          this.loading = false;
        });
      }
    });
  }

  initForm(): void {
    this.form = this.fb.group({
      company: this.fb.control(''),
      model: this.fb.control(''),
      modelType: this.fb.control(''),
      auction: this.fb.control(''),
      auctionDate: this.fb.control(''),
      code: this.fb.control(''),
      color: this.fb.control(''),
      mileageFrom: this.fb.control(''),
      mileageTo: this.fb.control(''),
      yearFrom: this.fb.control(''),
      yearTo: this.fb.control(''),
      rating: this.fb.control(''),
    });
  }

  ngOnInit(): void {
    this.title.setTitle('ECar JP Lots');
  }

  filter(): void {
    this.router.navigate([], {
      queryParams: {
        page: 1,
        r: Math.floor(Math.random() * 100),
        company: this.form.value.company || null,
        model: this.form.value.model || null,
        modelType: this.form.value.modelType || null,
        auction: this.form.value.auction || this.getTuesday(),
        auctionDate: this.form.value.auctionDate || null,
        code: this.form.value.code || null,
        color: this.form.value.color || null,
        mileageFrom: this.form.value.mileageFrom || null,
        mileageTo: this.form.value.mileageTo || null,
        yearFrom: this.form.value.yearFrom || null,
        yearTo: this.form.value.yearTo || null,
        rating: this.form.value.rating || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  createLotManually(): void {
    this.modalService.create({
      nzTitle: this.translate.instant('LOT_MANUALLY'),
      nzContent: AraiKenkiLotModalComponent,
      nzWidth: '550px',
    });
  }

  getTuesday(): string {
    const now = DateTime.now();
    return now.weekday < 4
      ? now.startOf('week').plus({ days: 1 }).toISODate()
      : now.plus({ days: 7 }).startOf('week').plus({ days: 1 }).toISODate();
  }

  getFilterFormState(params) {
    return {
      company: params.company,
      model: params.model,
      modelType: params.modelType,
      auction: params.auction || 'ARAI KENKI',
      auctionDate: params.auctionDate || this.getTuesday(),
      code: params.code,
      color: params.color,
      mileageFrom: params.mileageFrom,
      mileageTo: params.mileageTo,
      yearFrom: params.yearFrom,
      yearTo: params.yearTo,
      rating: params.rating,
    };
  }

  changePage(page: number): void {
    this.router.navigate(['manual-lots'], {
      queryParams: {
        page,
      },
      queryParamsHandling: 'merge',
    });
  }

  changePageSize(pageSize: number): void {
    this.loading = true;

    this.router.navigate(['manual-lots'], {
      queryParams: {
        pageSize,
      },
      queryParamsHandling: 'merge',
    });
  }

  sortEvent(sort: { key: string; value: string }): void {
    this.loading = true;

    this.router.navigate(['manual-lots'], {
      queryParams: {
        sort: sort.key,
        sortType: getTypeSort(sort.value),
      },
      queryParamsHandling: 'merge',
    });
  }

  removeFilter(): void {
    this.form.reset();
  }

  initDate(): void {
    this.lastDay = new Date();
    this.lastDay.setDate(this.lastDay.getDate() + 7);
    if (this.dateRange.length === 0) {
      const dates = getDaysRange(new Date(), new Date(this.lastDay));
      dates.map((item) => {
        this.dateRange.push({
          date: item,
          checked: this.selectedDays.indexOf(toLocalIsoString(this.getDate(item))) > -1,
        });
      });
    } else {
      this.dateRange.map((item) => {
        item.checked = this.selectedDays.indexOf(toLocalIsoString(this.getDate(item.date))) > -1;
      });
    }
  }

  getDate(date = null): Date {
    if (date) {
      return new Date(date);
    }

    return new Date();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
